import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Container } from 'reactstrap'
import { isEmpty, isEqual } from 'lodash'
import { sprintf } from 'sprintf-js'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import Icon from 'generics/Icon'

import { ACTIONS, I18N, USER } from 'constants/props'
import { COLORS } from 'constants/colors'
import * as notificationsActions from 'app_modules/notifications/actions'
import * as sessionSelectors from 'app_modules/session/selectors'
import currentNotification from 'app_modules/notifications/selectors'
import Translation from 'generics/Translation'

import styles from './notifications.scss'

const FADE_OUT_MS = 500
const DEFAULT_DELAY_S = 15

export class notifications extends Component {
  constructor(props) {
    super(props)
    this.state = {
      delay: DEFAULT_DELAY_S,
      isVisible: false,
    }
    this.hideTimer = null
    this.clearTimer = null
  }

  componentDidMount() {
    const { notification } = this.props

    if (Object.keys(notification).length) {
      this.showNotification(notification)
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { isVisible } = this.state
    return Object.keys(nextProps.notification).length > 0 || nextState.isVisible !== isVisible
  }

  componentDidUpdate(prevProps) {
    const { notification } = this.props
    const { delay } = this.state
    const { options } = notification

    if (options && options.delay && options.delay !== delay) {
      this.setState({ delay: options.delay })
    }

    if (isEmpty(notification)) {
      // Only animate out when a visible notification is cleared — not on every
      // parent re-render while the bar is already empty (e.g. route lazy load).
      if (!isEmpty(prevProps.notification)) {
        this.handleClose()
      }
    } else if (!isEqual(prevProps.notification, notification)) {
      this.showNotification(notification)
    }
  }

  componentWillUnmount() {
    this.clearTimers()
  }

  clearTimers = () => {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
    if (this.clearTimer) {
      clearTimeout(this.clearTimer)
      this.clearTimer = null
    }
  }

  showNotification = notification => {
    this.clearTimers()
    this.setState({ isVisible: true })

    const options = notification.options || {}
    const { delay } = this.state
    const delaySeconds = options.delay || delay || DEFAULT_DELAY_S

    if (options.persistent) {
      return
    }

    this.hideTimer = setTimeout(() => {
      this.handleClose()
    }, delaySeconds * 1000)
  }

  handleClose = () => {
    this.clearTimers()
    this.setState({ isVisible: false })

    const { actions } = this.props
    this.clearTimer = setTimeout(() => {
      actions.clearBarNotification()
    }, FADE_OUT_MS)
  }

  render() {
    const { className, i18n, testAttribute, notification, user } = this.props
    const { isVisible } = this.state

    const { label, message, title } = notification

    const options = {
      type: 'success',
      ...notification.options,
    }

    const { type } = options

    const notificationMessage = (label && i18n.notifications[label]) || message

    return (
      <div>
        <div
          className={classnames(
            styles.notifications,
            styles[type],
            { [styles['has-content']]: title || label || message },
            { [styles.notificationsVisible]: isVisible },
            className,
          )}
        >
          <Container>
            {title && (
              <span className={styles.title} title={title}>
                {title}
              </span>
            )}
            {notificationMessage && (
              <span className={styles.message} data-test={testAttribute} title={notificationMessage}>
                {notificationMessage}
              </span>
            )}
            {options?.btnAction ? <options.btnAction /> : null}
            {options?.showClose && (
              <Icon.Stroke7
                name="close"
                className={styles.crossIcon}
                onClick={() => {
                  this.handleClose()
                  if (notification.options?.onClose) {
                    notification.options.onClose()
                  }
                }}
                stroke={COLORS.grey1.rgba}
              />
            )}
          </Container>
        </div>

        {user && user.impersonateToken && (
          <div
            className={classnames(
              styles.notifications__impersonate,
              styles[type],
              { [styles['has-content']]: true },
              className,
            )}
          >
            <Container>
              {user.internalAdmin.name && (
                <span className={styles.title} title={user.internalAdmin.name}>
                  {sprintf(i18n.notifications.isImpersonatingTo, { internalAdminName: user.internalAdmin.name })}
                </span>
              )}
              <span className={styles.message} title={user.currentProfile.firstName}>
                {`${user.currentProfile.firstName} ${user.currentProfile.lastName}`}
              </span>
            </Container>
          </div>
        )}
      </div>
    )
  }
}

notifications.propTypes = {
  actions: ACTIONS.isRequired,
  className: PropTypes.string,
  testAttribute: PropTypes.string,
  i18n: I18N.isRequired,
  notification: PropTypes.shape({
    label: PropTypes.string,
    message: PropTypes.string,
    options: PropTypes.shape({
      delay: PropTypes.number,
      persistent: PropTypes.bool,
      onClose: PropTypes.func,
    }),
    title: PropTypes.string,
  }),
  user: USER,
}

notifications.defaultProps = {
  className: null,
  testAttribute: null,
  notification: {
    title: '',
    message: '',
    options: {},
  },
  user: null,
}

const mapStateToProps = state => ({
  notification: currentNotification(state),
  user: sessionSelectors.user(state),
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(notificationsActions, dispatch),
})

export default Translation(connect(mapStateToProps, mapDispatchToProps)(notifications), ['notifications'])
